import React from 'react'
import { Check, ChevronRight } from 'lucide-react'

interface LeadStageProgressProps {
  currentStatus: string
  onStatusChange: (newStatus: string) => void
  disabled?: boolean
}

type LeadStage = 'New' | 'Working' | 'Qualified' | 'Disqualified'

const STAGE_ORDER: LeadStage[] = ['New', 'Working']
const FINAL_STAGES: LeadStage[] = ['Qualified', 'Disqualified']

export function LeadStageProgress({
  currentStatus,
  onStatusChange,
  disabled = false,
}: LeadStageProgressProps) {
  const currentStage = currentStatus as LeadStage
  const currentIndex = STAGE_ORDER.indexOf(currentStage)
  const isFinalStage = FINAL_STAGES.includes(currentStage)

  const isStageCompleted = (stage: LeadStage): boolean => {
    const stageIndex = STAGE_ORDER.indexOf(stage)
    if (stageIndex === -1) return false
    return currentIndex > stageIndex || isFinalStage
  }

  const isStageActive = (stage: LeadStage): boolean => {
    return stage === currentStage
  }

  const canProgressToStage = (stage: LeadStage): boolean => {
    if (disabled) return false

    // Can't click on the current stage
    if (stage === currentStage) return false

    // If we're at a final stage (Qualified/Disqualified), can go back to any stage
    if (isFinalStage) return true

    // Can move between any stages
    return true
  }

  const handleStageClick = (stage: LeadStage) => {
    if (canProgressToStage(stage)) {
      onStatusChange(stage)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, stage: LeadStage) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleStageClick(stage)
    }
  }

  const getStageStyles = (stage: LeadStage): string => {
    if (isStageActive(stage)) {
      if (stage === 'Qualified') {
        return 'bg-accent/10 text-accent border-accent ring-2 ring-accent'
      } else if (stage === 'Disqualified') {
        return 'bg-destructive/10 text-destructive border-destructive ring-2 ring-destructive'
      } else {
        return 'bg-primary/10 text-primary border-primary ring-2 ring-primary'
      }
    }

    if (isStageCompleted(stage)) {
      return 'bg-gray-100 text-gray-600 border-gray-300'
    }

    if (canProgressToStage(stage)) {
      if (stage === 'Qualified') {
        return 'bg-white text-accent border-accent/30 hover:bg-accent/5 cursor-pointer'
      } else if (stage === 'Disqualified') {
        return 'bg-white text-destructive border-destructive/30 hover:bg-destructive/5 cursor-pointer'
      } else {
        return 'bg-white text-primary border-primary/30 hover:bg-primary/5 cursor-pointer'
      }
    }

    return 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
  }

  const renderStageButton = (stage: LeadStage, showArrow: boolean = true) => {
    const isActive = isStageActive(stage)
    const isCompleted = isStageCompleted(stage)
    const canProgress = canProgressToStage(stage)

    return (
      <React.Fragment key={stage}>
        <button
          onClick={() => handleStageClick(stage)}
          onKeyDown={(e) => handleKeyDown(e, stage)}
          disabled={!canProgress}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-all
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
            ${getStageStyles(stage)}
          `}
          aria-label={`${stage} stage${isActive ? ' (current)' : ''}${canProgress ? ' - click to advance' : ''}`}
          aria-current={isActive ? 'step' : undefined}
          tabIndex={canProgress ? 0 : -1}
        >
          {isCompleted && !isActive && <Check className="w-4 h-4" aria-hidden="true" />}
          <span className="font-medium">{stage}</span>
        </button>
        {showArrow && (
          <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
        )}
      </React.Fragment>
    )
  }

  return (
    <nav
      className="bg-white border rounded-lg p-4 shadow-sm"
      aria-label="Lead stage progression"
      role="navigation"
    >
      <div className="flex items-center justify-start gap-3 flex-wrap">
        {/* Main progression: New -> Working */}
        {STAGE_ORDER.map((stage, index) =>
          renderStageButton(stage, index < STAGE_ORDER.length - 1)
        )}

        {/* Final stages branching from Working */}
        {(currentStage === 'Working' || isFinalStage) && (
          <>
            <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
            <div className="flex items-center gap-3">
              {renderStageButton('Qualified', false)}
              <span className="text-gray-400 font-medium" aria-hidden="true">
                /
              </span>
              {renderStageButton('Disqualified', false)}
            </div>
          </>
        )}
      </div>

      {/* Screen reader only text for context */}
      <div className="sr-only">
        Lead progression: New to Working, then either Qualified or Disqualified. Current stage:{' '}
        {currentStage}. You can move between any stages. Use Tab to navigate and Enter to change
        stages.
      </div>
    </nav>
  )
}
